import { TypeChecker } from '@attain-apps/type-guards'

import type { CashDraw } from '../cash-draw'
import type { DocumentNumber } from '../document-number'
import * as payment from '../payment'
import type { ProjectId } from '../project-id'
import type { Subaccount } from '../subaccount'

import type { CurrentMonthBalance } from './current-month-balance'
import type { WorkbookData } from './workbook-data'

/**
 * Lookup from normalized BCH payment/document identifiers to document metadata.
 *
 * This resolves the BCH outline's `DOC #`, `OH Rate`, drawdown amount, award
 * end date, and `PMS Upload` row identifiers before justification text is
 * generated.
 */
export type DocumentLookup = Readonly<
  Record<payment.Identifier, DocumentLookup.Entry>
>
export namespace DocumentLookup {
  export interface Entry {
    readonly key: payment.Identifier
    readonly documentNumber: DocumentNumber
    readonly projectId?: ProjectId
    readonly projectIds: readonly ProjectId[]
    readonly paymentType?: payment.Type
    readonly subacctCode?: Subaccount.Code
    readonly ohRate?: number
    readonly ohRatesByProject?: Readonly<Record<ProjectId, number>>
    readonly drawdownAmount?: number
    readonly awardEndDate?: string
    readonly pmsUploadRowIndex?: number
  }
  export namespace Entry {
    export const isEntry: TypeChecker<Entry> = TypeChecker.Factory.object({
      awardEndDate: TypeChecker.Factory.optional(TypeChecker.isString),
      documentNumber: TypeChecker.isString,
      drawdownAmount: TypeChecker.Factory.optional(TypeChecker.isNumber),
      key: TypeChecker.isString,
      ohRate: TypeChecker.Factory.optional(TypeChecker.isNumber),
      ohRatesByProject: TypeChecker.Factory.optional(
        TypeChecker.Factory.record(TypeChecker.isString, TypeChecker.isNumber),
      ),
      paymentType: TypeChecker.Factory.optional(TypeChecker.isString),
      pmsUploadRowIndex: TypeChecker.Factory.optional(TypeChecker.isNumber),
      projectId: TypeChecker.Factory.optional(TypeChecker.isString),
      projectIds: TypeChecker.Factory.list(TypeChecker.isString),
      subacctCode: TypeChecker.Factory.optional(TypeChecker.isString),
    })
  }

  export const isDocumentLookup: TypeChecker<DocumentLookup> =
    TypeChecker.Factory.record(TypeChecker.isString, Entry.isEntry)

  /**
   * Builds document/payment metadata from parsed workbook data and calculated
   * balances.
   */
  export function fromWorkbookData(
    data: Readonly<WorkbookData>,
    balances: readonly CurrentMonthBalance[],
  ): DocumentLookup {
    /**
     * Newest `PACCT` metadata by project; used to resolve each balance project
     * to its `Document #`, `OH Rate`, and project dates.
     */
    const pacctByProject = newestPacctRowsByProject(data.cashDraw.pacct)
    /**
     * `PMS Upload` rows by normalized `Payment Type`; this is the preferred
     * payment-row match when it contains a document-like identifier.
     */
    const pmsByPaymentType = pmsUploadRowsByPaymentType(data.cashDraw.pmsUpload)
    /**
     * `PMS Upload` rows by normalized `Subacct Code`; this is the fallback
     * payment-row match when `Payment Type` is generic, such as "S".
     */
    const pmsBySubacctCode = pmsUploadRowsBySubacctCode(data.cashDraw.pmsUpload)
    /**
     * `PMS Download` drawdown totals by document number; attached to each
     * document entry so justification generation can compare requested funds.
     * PMS Download is the source because it reflects the draw validations,
     * such as reconciling items moved to the recon with their amount zeroed.
     */
    const drawdownAmountsByDocument = drawdownAmountsByDocumentNumber(
      data.cashDraw.pmsDownload,
    )
    /**
     * Award end dates by document number from the subaccount summary; used to
     * carry expired/missing award context forward.
     */
    const awardEndDatesByDocument = awardEndDatesByDocumentNumber(data)
    /**
     * Document entries keyed by normalized document/payment id; multiple
     * balance projects can merge into the same document entry.
     */
    const entries = new Map<payment.Identifier, Entry>()
    const projectIds = balanceProjectIds(balances)

    /**
     * Build one document entry per normalized document/payment id.
     *
     * Balances are grouped by project, but the final justification workflow is
     * driven by document/PMS Upload identifiers. This loop walks each balanced
     * project, resolves its newest PACCT document, then merges project metadata
     * into the document entry. Merging matters because one document can cover
     * multiple projects and therefore multiple OH rates.
     */
    for (const projectId of projectIds) {
      const pacctRow = pacctByProject.get(projectId)
      const key = payment.Identifier.from(pacctRow?.documentNumber)
      if (!pacctRow?.documentNumber || !key) {
        continue
      }

      const existingEntry = entries.get(key)
      const pmsUploadRow =
        /**
         * Prefer a PMS row whose `Payment Type` matches the document key, then
         * fall back to `Subacct Code`; in the provided BCH file, `Payment Type`
         * is often just "S", while `Subacct Code` carries the document value.
         */
        pmsByPaymentType.get(key) ?? pmsBySubacctCode.get(key)
      /**
       * Preserve project-specific OH rates while keeping the existing singular
       * `ohRate` field available for documents that only have one rate.
       */
      const ohRatesByProject = {
        ...(existingEntry?.ohRatesByProject ?? {}),
        ...(pacctRow.ohRate === undefined
          ? {}
          : { [projectId]: pacctRow.ohRate }),
      }

      entries.set(key, {
        awardEndDate:
          awardEndDatesByDocument.get(key) ?? existingEntry?.awardEndDate,
        documentNumber:
          existingEntry?.documentNumber ?? pacctRow.documentNumber,
        drawdownAmount:
          drawdownAmountsByDocument.get(key) ?? existingEntry?.drawdownAmount,
        key,
        ohRate: existingEntry?.ohRate ?? pacctRow.ohRate,
        ohRatesByProject: Object.keys(ohRatesByProject).length
          ? ohRatesByProject
          : undefined,
        paymentType: pmsUploadRow?.paymentType ?? existingEntry?.paymentType,
        pmsUploadRowIndex:
          pmsUploadRow?.rowIndex ?? existingEntry?.pmsUploadRowIndex,
        projectId: existingEntry?.projectId ?? projectId,
        projectIds: [
          ...new Set([...(existingEntry?.projectIds ?? []), projectId]),
        ].sort((a, b) => a.localeCompare(b)),
        subacctCode: pmsUploadRow?.subacctCode ?? existingEntry?.subacctCode,
      })
    }

    const lookup: Record<payment.Identifier, Entry> = {}
    for (const [key, entry] of [...entries.entries()].sort(([keyA], [keyB]) =>
      keyA.localeCompare(keyB),
    )) {
      lookup[key] = entry
    }
    return lookup
  }

  /**
   * Gets unique project ids that have calculated balances.
   */
  function balanceProjectIds(balances: readonly CurrentMonthBalance[]) {
    return [...new Set(balances.map(balance => balance.projectId))].sort(
      (a, b) => a.localeCompare(b),
    )
  }

  /**
   * Finds the newest PACCT metadata row with a document number for each
   * project.
   */
  function newestPacctRowsByProject(
    rows: readonly CashDraw.PacctRow[],
  ): ReadonlyMap<ProjectId, CashDraw.PacctRow> {
    const rowsByProject = new Map<ProjectId, CashDraw.PacctRow[]>()
    for (const row of rows) {
      rowsByProject.set(row.projectId, [
        ...(rowsByProject.get(row.projectId) ?? []),
        row,
      ])
    }

    const latestRows = new Map<ProjectId, CashDraw.PacctRow>()
    for (const [projectId, projectRows] of rowsByProject.entries()) {
      // The outline says to sort PACCT by newest projects first, then use the
      // first non-empty document number. Project End Date is the primary sort;
      // Effective Date breaks ties.
      const latestRow = [...projectRows]
        .sort(comparePacctRowsNewestFirst)
        .find(row => row.documentNumber)
      if (latestRow) {
        latestRows.set(projectId, latestRow)
      }
    }
    return latestRows
  }

  /**
   * Sorts PACCT rows by `Project End Date`, then `Effective Date`, newest
   * first.
   */
  function comparePacctRowsNewestFirst(
    left: CashDraw.PacctRow,
    right: CashDraw.PacctRow,
  ) {
    return (
      compareOptionalDatesNewestFirst(
        left.projectEndDate,
        right.projectEndDate,
      ) ||
      compareOptionalDatesNewestFirst(left.effectiveDate, right.effectiveDate)
    )
  }

  /**
   * Compares normalized ISO-like dates with missing dates treated as oldest.
   */
  function compareOptionalDatesNewestFirst(
    left: string | undefined,
    right: string | undefined,
  ) {
    return (right ?? '').localeCompare(left ?? '')
  }

  /**
   * Indexes PMS Upload rows by normalized `Payment Type`.
   */
  function pmsUploadRowsByPaymentType(rows: readonly CashDraw.PmsUploadRow[]) {
    const rowsByPaymentType = new Map<
      payment.Identifier,
      CashDraw.PmsUploadRow
    >()
    for (const row of rows) {
      const key = payment.Identifier.from(row.paymentType)
      if (key && !rowsByPaymentType.has(key)) {
        rowsByPaymentType.set(key, row)
      }
    }
    return rowsByPaymentType
  }

  /**
   * Indexes PMS Upload rows by normalized `Subacct Code`.
   */
  function pmsUploadRowsBySubacctCode(rows: readonly CashDraw.PmsUploadRow[]) {
    const rowsBySubacctCode = new Map<
      payment.Identifier,
      CashDraw.PmsUploadRow
    >()
    for (const row of rows) {
      const key = payment.Identifier.from(row.subacctCode)
      if (key && !rowsBySubacctCode.has(key)) {
        rowsBySubacctCode.set(key, row)
      }
    }
    return rowsBySubacctCode
  }

  /**
   * Indexes PMS Download drawdown amounts by normalized document number.
   */
  function drawdownAmountsByDocumentNumber(
    rows: readonly CashDraw.PmsDownloadRow[],
  ) {
    const amountsByDocument = new Map<payment.Identifier, number>()
    for (const row of rows) {
      const key = payment.Identifier.from(row.documentNumber)
      if (!key) {
        continue
      }
      amountsByDocument.set(
        key,
        (amountsByDocument.get(key) ?? 0) + row.drawdownAmount,
      )
    }
    return amountsByDocument
  }

  /**
   * Indexes award end dates by normalized document number.
   */
  function awardEndDatesByDocumentNumber(data: Readonly<WorkbookData>) {
    const datesByDocument = new Map<payment.Identifier, string>()
    for (const row of data.subaccountSummary) {
      setAwardEndDateIfPresent(datesByDocument, row.documentNumber, row)
    }
    return datesByDocument
  }

  /**
   * Stores the first available award end date for a document.
   */
  function setAwardEndDateIfPresent(
    datesByDocument: Map<payment.Identifier, string>,
    documentNumber: DocumentNumber,
    row: { readonly awardEndDate?: string },
  ) {
    const key = payment.Identifier.from(documentNumber)
    if (key && row.awardEndDate && !datesByDocument.has(key)) {
      datesByDocument.set(key, row.awardEndDate)
    }
  }
}
